/**
 * useFlightLookup — the travel flight-lookup state the studios' Step-2 travel
 * forms share (one-retry-on-503, error classification). Presentational callers
 * read `flight`/`lookupError`/`retrying` and call `lookup(iata, date)`; they own
 * their own RHF instance and pass the current field values in.
 */

import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';

import { ApiClientError, lookupFlight } from '../../api/client';
import { useT } from '../../i18n';
import { describeApiError } from '../../lib/apiErrors';
import type { FlightLookup } from '../../types';

export interface FlightLookupError {
  message: string;
  code: 'BUSY' | 'NOT_FOUND' | 'OTHER';
}

export interface UseFlightLookup {
  flight: FlightLookup | null;
  setFlight: (f: FlightLookup | null) => void;
  lookupError: FlightLookupError | null;
  setLookupError: (e: FlightLookupError | null) => void;
  retrying: boolean;
  isPending: boolean;
  lookup: (iata: string, date: string) => void;
}

export function useFlightLookup(): UseFlightLookup {
  const t = useT();
  const [flight, setFlight] = useState<FlightLookup | null>(null);
  const [lookupError, setLookupError] = useState<FlightLookupError | null>(null);
  const [retrying, setRetrying] = useState(false);

  const mutation = useMutation({
    mutationFn: async (vars: { iata: string; date: string }): Promise<FlightLookup> => {
      try {
        return await lookupFlight(vars.iata, vars.date);
      } catch (e) {
        if (
          e instanceof ApiClientError &&
          (e.status === 503 || e.body.code === 'FLIGHT_ENGINE_BUSY')
        ) {
          setRetrying(true);
          await new Promise((r) => setTimeout(r, 4000));
          try {
            return await lookupFlight(vars.iata, vars.date);
          } finally {
            setRetrying(false);
          }
        }
        throw e;
      }
    },
    onSuccess: (data) => {
      setFlight(data);
      setLookupError(null);
    },
    onError: (e) => {
      setFlight(null);
      const msg = describeApiError(e, t);
      const code: 'BUSY' | 'NOT_FOUND' | 'OTHER' =
        e instanceof ApiClientError && (e.status === 503 || e.body.code === 'FLIGHT_ENGINE_BUSY')
          ? 'BUSY'
          : e instanceof ApiClientError && e.body.code === 'FLIGHT_NOT_FOUND'
            ? 'NOT_FOUND'
            : 'OTHER';
      setLookupError({ message: msg, code });
    },
  });

  return {
    flight,
    setFlight,
    lookupError,
    setLookupError,
    retrying,
    isPending: mutation.isPending,
    lookup: (iata, date) => mutation.mutate({ iata, date }),
  };
}
